import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Edit, Trash2, MapPin, Search, Hotel, Camera, Star, Copy, Globe, Map, Image as ImageIcon, X, Car, Moon, ChevronDown, ChevronUp, Save, CheckSquare, XSquare, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import DashboardLayout from "../../components/layouts/DashboardLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICLE_TYPES = [
  "Sedan", "Ertiga", "Innova", "Innova Crysta",
  "Tempo Traveller 12 Seater", "Tempo Traveller 17 Seater",
  "Mini Bus 20 Seater", "Bus 35 Seater", "Bus 45 Seater"
];

const AdminResources = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("destinations");
  const [sightseeing, setSightseeing] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [taxiPricing, setTaxiPricing] = useState([]);
  const [entryTickets, setEntryTickets] = useState([]);
  const [packageTemplates, setPackageTemplates] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSightseeing, setSavingSightseeing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  
  // Sightseeing sub-tabs
  const [sightseeingRegion, setSightseeingRegion] = useState("domestic");
  const [expandedDestinations, setExpandedDestinations] = useState({});
  
  // Entry Tickets expanded destinations
  const [expandedEntryDestinations, setExpandedEntryDestinations] = useState({});
  
  // Package Templates sub-tabs and expanded destinations
  const [packageTemplateRegion, setPackageTemplateRegion] = useState("domestic");
  const [expandedTemplateDestinations, setExpandedTemplateDestinations] = useState({});
  
  // Hotels expanded destinations
  const [expandedHotelDestinations, setExpandedHotelDestinations] = useState({});
  
  // Forms
  const [showModal, setShowModal] = useState(false);
  const [showDestModal, setShowDestModal] = useState(false);
  const [showTaxiModal, setShowTaxiModal] = useState(false);
  const [showEntryTicketModal, setShowEntryTicketModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showHotelModal, setShowHotelModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  // Sightseeing Form
  const [ssForm, setSsForm] = useState({
    destination: "", tour_name: "", duration: "", category: "",
    description: "", price_adult: "", price_child: "", type: "Private",
    region_type: "domestic", inclusions: "", exclusions: "", notes: "", image: "",
    kms: ""
  });

  // Entry Ticket Form
  const [entryTicketForm, setEntryTicketForm] = useState({
    destination: "",
    attraction_name: "",
    ticket_type: "Standard",
    price_adult: 0,
    price_cwb: 0,
    price_cnb: 0,
    currency: "INR",
    validity: "Single Entry",
    description: "",
    inclusions: "",
    notes: "",
    status: "active",
    region_type: "domestic",
    rate_periods: [{
      start_date: "",
      end_date: "",
      valid_days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
    }]
  });

  // Package Template Form (Inclusions & Exclusions)
  const [templateForm, setTemplateForm] = useState({
    destination: "",
    region_type: "domestic",
    inclusions: "",
    exclusions: "",
    status: "active"
  });

  // Hotel Form - Each rate_periods contains date range + room pricing + extra pricing
  const [hotelForm, setHotelForm] = useState({
    name: "",
    destination: "",
    city: "",
    category: "3 Star",
    rate_periods: [{
      from_date: "",
      to_date: "",
      valid_days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      room_pricing: [{ room_category: "Deluxe Room", ep: "", cp: "", map: "", ap: "" }],
      extra_bed: { ep: "", cp: "", map: "", ap: "" },
      cwb: { ep: "", cp: "", map: "", ap: "" },
      cnb: { ep: "", cp: "", map: "", ap: "" }
    }],
    amenities: "",
    status: "active"
  });

  // Destination Form
  const [destForm, setDestForm] = useState({
    country: "",
    name: "", // State/Region
    cities: "", // Comma separated for input
    region_type: "domestic",
    status: "active"
  });

  // Destinations Domestic/International sub-tab
  const [destinationRegion, setDestinationRegion] = useState("domestic");

  // Taxi Pricing Form
  const [taxiForm, setTaxiForm] = useState({
    destination: "",
    nights: 1,
    vehicles: [{ vehicle_type: "Sedan", price: 0 }]
  });

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === "sightseeing") {
        const res = await axios.get(`${API}/sightseeing?destination=${searchTerm}`);
        setSightseeing(res.data);
        // Also fetch destinations for the dropdown
        const destRes = await axios.get(`${API}/destinations`);
        setDestinations(destRes.data);
      } else if (activeTab === "destinations") {
        const res = await axios.get(`${API}/destinations`);
        setDestinations(res.data);
      } else if (activeTab === "taxi") {
        const res = await axios.get(`${API}/taxi-pricing`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTaxiPricing(res.data);
      } else if (activeTab === "entrytickets") {
        const res = await axios.get(`${API}/entry-tickets`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEntryTickets(res.data);
      } else if (activeTab === "templates") {
        const res = await axios.get(`${API}/package-templates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPackageTemplates(res.data);
      } else if (activeTab === "hotels") {
        const res = await axios.get(`${API}/hotels`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHotels(res.data);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  const handleDelete = async (id, type) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/${type}/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Sightseeing Logic
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image size too large (max 2MB)");
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setSsForm(prev => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const openEditSightseeing = (item) => {
    setEditingItem(item);
    setSsForm({
        destination: item.destination || "",
        tour_name: item.tour_name || "",
        duration: item.duration || "",
        category: item.category || "",
        description: item.description || "",
        price_adult: item.price_adult ?? "",
        price_child: item.price_child ?? "",
        type: item.type || "Private",
        region_type: item.region_type || "domestic",
        inclusions: item.inclusions || "",
        exclusions: item.exclusions || "",
        notes: item.notes || "",
        image: item.image || "",
        kms: item.kms || ""
    });
    setShowModal(true);
  };

  // Re-fetches just the sightseeing list (skipping the unrelated /destinations call that
  // fetchData() also makes) so post-save refresh isn't stuck behind a second round-trip.
  const fetchSightseeingList = async () => {
    const res = await axios.get(`${API}/sightseeing?destination=${searchTerm}`);
    setSightseeing(res.data);
  };

  const submitSightseeing = async (e) => {
    e.preventDefault();
    if (savingSightseeing) return;
    const payload = {
        ...ssForm,
        price_adult: parseFloat(ssForm.price_adult) || 0,
        price_child: parseFloat(ssForm.price_child) || 0
    };

    setSavingSightseeing(true);
    try {
        if (editingItem) {
            await axios.put(`${API}/sightseeing/${editingItem.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Sightseeing updated!");
        } else {
            await axios.post(`${API}/sightseeing`, payload, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Sightseeing added!");
        }
        setShowModal(false);
        setEditingItem(null);
        setSsForm({
            destination: "", tour_name: "", duration: "", category: "",
            description: "", price_adult: "", price_child: "", type: "Private",
            region_type: "domestic", inclusions: "", exclusions: "", notes: "", image: "",
            kms: ""
        });
        await fetchSightseeingList();
    } catch (error) {
        const detail = error.response?.data?.detail;
        const msg = Array.isArray(detail)
          ? detail.map(d => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join(', ')
          : (typeof detail === 'string' ? detail : "Failed to save");
        toast.error(msg);
    } finally {
        setSavingSightseeing(false);
    }
  };

  // Toggle destination expansion in sightseeing
  const toggleDestinationExpand = (dest) => {
    setExpandedDestinations(prev => ({ ...prev, [dest]: !prev[dest] }));
  };

  // Group sightseeing by region and destination
  const getGroupedSightseeing = () => {
    const filtered = sightseeing.filter(item => 
      (item.region_type || "domestic") === sightseeingRegion &&
      (searchTerm === "" || item.destination.toLowerCase().includes(searchTerm.toLowerCase()) || item.tour_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    return filtered.reduce((acc, item) => {
      const dest = item.destination;
      if (!acc[dest]) acc[dest] = [];
      acc[dest].push(item);
      return acc;
    }, {});
  };

  const groupedSightseeing = getGroupedSightseeing();

  // Destination Logic
  const submitDestination = async (e) => {
    e.preventDefault();
    const region_type = (destForm.region_type || "domestic").toLowerCase();
    // Keep `country` aligned with region_type to avoid mismatched UI badges.
    const country = destForm.country?.trim()
      ? destForm.country
      : (region_type === "international" ? "International" : "Domestic");
    const payload = {
        ...destForm,
        country,
        region_type,
        cities: destForm.cities.split(',').map(c => c.trim()).filter(c => c)
    };
    try {
        if (editingItem) {
            await axios.put(`${API}/destinations/${editingItem.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Destination updated!");
        } else {
            await axios.post(`${API}/destinations`, payload, { headers: { Authorization: `Bearer ${token}` } });
            toast.success("Destination added!");
        }
        setShowDestModal(false);
        setEditingItem(null);
        setDestForm({ country: "", name: "", cities: "", region_type: "domestic", status: "active" });
        fetchData();
    } catch (error) {
        toast.error("Failed to save destination");
    }
  };

  const openEditDest = (item) => {
      setEditingItem(item);
      const inferredRegion = (item.region_type || ((item.country || '').toLowerCase() === 'international' ? 'international' : 'domestic'));
      setDestForm({
          country: item.country || "",
          name: item.name || "",
          cities: (item.cities || []).join(", "),
          region_type: inferredRegion,
          status: item.status || "active"
      });
      setShowDestModal(true);
  };

  const toggleDestStatus = async (item) => {
      const newStatus = item.status === 'active' ? 'inactive' : 'active';
      try {
          await axios.put(`${API}/destinations/${item.id}`, { ...item, status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
          fetchData();
          toast.success(`Destination ${newStatus}`);
      } catch (error) {
          toast.error("Failed to update status");
      }
  };

  // Taxi Pricing Logic
  const toggleRow = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const addVehicle = () => {
    const usedTypes = taxiForm.vehicles.map(v => v.vehicle_type);
    const availableType = VEHICLE_TYPES.find(t => !usedTypes.includes(t)) || VEHICLE_TYPES[0];
    setTaxiForm({
      ...taxiForm,
      vehicles: [...taxiForm.vehicles, { vehicle_type: availableType, price: 0 }]
    });
  };

  const removeVehicle = (index) => {
    const newVehicles = taxiForm.vehicles.filter((_, i) => i !== index);
    setTaxiForm({ ...taxiForm, vehicles: newVehicles });
  };

  const updateVehicle = (index, field, value) => {
    const newVehicles = [...taxiForm.vehicles];
    newVehicles[index] = { ...newVehicles[index], [field]: field === 'price' ? parseFloat(value) || 0 : value };
    setTaxiForm({ ...taxiForm, vehicles: newVehicles });
  };

  const resetTaxiForm = () => {
    setTaxiForm({ destination: "", nights: 1, vehicles: [{ vehicle_type: "Sedan", price: 0 }] });
    setEditingItem(null);
  };

  const submitTaxiPricing = async () => {
    if (!taxiForm.destination || taxiForm.nights < 1) {
      toast.error("Please fill destination and nights");
      return;
    }
    if (taxiForm.vehicles.length === 0 || taxiForm.vehicles.some(v => !v.vehicle_type || v.price <= 0)) {
      toast.error("Please add at least one vehicle with valid price");
      return;
    }

    try {
      if (editingItem) {
        await axios.put(`${API}/taxi-pricing/${editingItem.id}`, taxiForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Pricing updated!");
      } else {
        await axios.post(`${API}/taxi-pricing`, taxiForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Pricing added!");
      }
      setShowTaxiModal(false);
      resetTaxiForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save pricing");
    }
  };

  const handleEditTaxi = (item) => {
    setTaxiForm({
      destination: item.destination,
      nights: item.nights,
      vehicles: item.vehicles || []
    });
    setEditingItem(item);
    setShowTaxiModal(true);
  };

  const handleDeleteTaxi = async (id) => {
    if (!window.confirm("Delete this pricing?")) return;
    try {
      await axios.delete(`${API}/taxi-pricing/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Group taxi pricing by destination
  const groupedTaxiPricing = taxiPricing.reduce((acc, item) => {
    if (!acc[item.destination]) acc[item.destination] = [];
    acc[item.destination].push(item);
    return acc;
  }, {});

  const filteredTaxiDestinations = Object.keys(groupedTaxiPricing).filter(dest =>
    dest.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString()}`;

  // Entry Ticket Functions
  const resetEntryTicketForm = () => {
    setEntryTicketForm({
      destination: "",
      attraction_name: "",
      ticket_type: "Standard",
      price_adult: 0,
      price_cwb: 0,
      price_cnb: 0,
      currency: "INR",
      validity: "Single Entry",
      description: "",
      inclusions: "",
      notes: "",
      status: "active",
      region_type: "domestic",
      rate_periods: [{
        start_date: "",
        end_date: "",
        valid_days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
      }]
    });
    setEditingItem(null);
  };

  const addEntryTicketRatePeriod = () => {
    setEntryTicketForm(prev => ({
      ...prev,
      rate_periods: [...(prev.rate_periods || []), {
        start_date: "",
        end_date: "",
        valid_days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
      }]
    }));
  };

  const removeEntryTicketRatePeriod = (idx) => {
    setEntryTicketForm(prev => ({
      ...prev,
      rate_periods: (prev.rate_periods || []).filter((_, i) => i !== idx)
    }));
  };

  const updateEntryTicketRatePeriod = (idx, field, value) => {
    setEntryTicketForm(prev => ({
      ...prev,
      rate_periods: (prev.rate_periods || []).map((p, i) => i === idx ? { ...p, [field]: value } : p)
    }));
  };

  const toggleEntryTicketValidDay = (idx, day) => {
    setEntryTicketForm(prev => ({
      ...prev,
      rate_periods: (prev.rate_periods || []).map((p, i) => {
        if (i !== idx) return p;
        const current = p.valid_days || [];
        const next = current.includes(day) ? current.filter(d => d !== day) : [...current, day];
        return { ...p, valid_days: next };
      })
    }));
  };

  const submitEntryTicket = async () => {
    if (!entryTicketForm.destination || !entryTicketForm.attraction_name) {
      toast.error("Please fill destination and attraction name");
      return;
    }

    try {
      const payload = {
        ...entryTicketForm,
        price_adult: parseFloat(entryTicketForm.price_adult) || 0,
        price_cwb: parseFloat(entryTicketForm.price_cwb) || 0,
        price_cnb: parseFloat(entryTicketForm.price_cnb) || 0
      };

      if (editingItem) {
        await axios.put(`${API}/entry-tickets/${editingItem.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Entry ticket updated!");
      } else {
        await axios.post(`${API}/entry-tickets`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Entry ticket added!");
      }
      setShowEntryTicketModal(false);
      resetEntryTicketForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save entry ticket");
    }
  };

  const handleEditEntryTicket = (item) => {
    setEntryTicketForm({
      destination: item.destination,
      attraction_name: item.attraction_name,
      ticket_type: item.ticket_type || "Standard",
      price_adult: item.price_adult || 0,
      price_cwb: item.price_cwb || 0,
      price_cnb: item.price_cnb || 0,
      currency: item.currency || "INR",
      validity: item.validity || "Single Entry",
      description: item.description || "",
      inclusions: item.inclusions || "",
      notes: item.notes || "",
      status: item.status || "active",
      region_type: item.region_type || "domestic",
      rate_periods: (item.rate_periods && item.rate_periods.length > 0)
        ? item.rate_periods.map(p => ({
            start_date: p.start_date || "",
            end_date: p.end_date || "",
            valid_days: p.valid_days || ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]
          }))
        : [{ start_date: "", end_date: "", valid_days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] }]
    });
    setEditingItem(item);
    setShowEntryTicketModal(true);
  };

  const handleDeleteEntryTicket = async (id) => {
    if (!window.confirm("Delete this entry ticket?")) return;
    try {
      await axios.delete(`${API}/entry-tickets/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Deleted!");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Toggle entry ticket destination expansion
  const toggleEntryDestinationExpand = (dest) => {
    setExpandedEntryDestinations(prev => ({ ...prev, [dest]: !prev[dest] }));
  };

  // Group entry tickets by destination
  const groupedEntryTickets = entryTickets.reduce((acc, item) => {
    const dest = item.destination;
    if (!acc[dest]) acc[dest] = [];
    acc[dest].push(item);
    return acc;
  }, {});

  const filteredEntryDestinations = Object.keys(groupedEntryTickets).filter(dest =>
    dest.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ============ PACKAGE TEMPLATES (Inclusions & Exclusions) LOGIC ============
  
  const resetTemplateForm = () => {
    setTemplateForm({
      destination: "",
      region_type: packageTemplateRegion,
      inclusions: "",
      exclusions: "",
      status: "active"
    });
    setEditingItem(null);
  };

  const submitTemplate = async () => {
    if (!templateForm.destination) {
      toast.error("Please enter a destination");
      return;
    }
    try {
      if (editingItem) {
        await axios.put(`${API}/package-templates/${editingItem.id}`, templateForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Template updated!");
      } else {
        await axios.post(`${API}/package-templates`, templateForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Template added!");
      }
      setShowTemplateModal(false);
      resetTemplateForm();
      fetchData();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error("Failed to save template");
    }
  };

  const openEditTemplate = (item) => {
    setEditingItem(item);
    setTemplateForm({
      destination: item.destination,
      region_type: item.region_type || "domestic",
      inclusions: item.inclusions || "",
      exclusions: item.exclusions || "",
      status: item.status || "active"
    });
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/package-templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Template deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete template");
    }
  };

  const toggleTemplateDestinationExpand = (dest) => {
    setExpandedTemplateDestinations(prev => ({ ...prev, [dest]: !prev[dest] }));
  };

  // Group package templates by region and destination
  const getGroupedTemplates = () => {
    const filtered = packageTemplates.filter(item =>
      (item.region_type || "domestic") === packageTemplateRegion &&
      (searchTerm === "" || item.destination.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    return filtered.reduce((acc, item) => {
      const dest = item.destination;
      if (!acc[dest]) acc[dest] = [];
      acc[dest].push(item);
      return acc;
    }, {});
  };

  const groupedTemplates = getGroupedTemplates();

  // ============ HOTELS LOGIC ============
  
  const resetHotelForm = () => {
    setHotelForm({
      name: "",
      destination: "",
      city: "",
      category: "3 Star",
      rate_periods: [{
        from_date: "",
        to_date: "",
        valid_days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        room_pricing: [{ room_category: "Deluxe Room", ep: "", cp: "", map: "", ap: "" }],
        extra_bed: { ep: "", cp: "", map: "", ap: "" },
        cwb: { ep: "", cp: "", map: "", ap: "" },
        cnb: { ep: "", cp: "", map: "", ap: "" }
      }],
      amenities: "",
      status: "active"
    });
    setEditingItem(null);
  };

  // Add a new rate period (clone of date range + room pricing)
  const addRatePeriod = () => {
    setHotelForm(prev => ({
      ...prev,
      rate_periods: [...prev.rate_periods, {
        from_date: "",
        to_date: "",
        valid_days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
        room_pricing: [{ room_category: "Deluxe Room", ep: "", cp: "", map: "", ap: "" }],
        extra_bed: { ep: "", cp: "", map: "", ap: "" },
        cwb: { ep: "", cp: "", map: "", ap: "" },
        cnb: { ep: "", cp: "", map: "", ap: "" }
      }]
    }));
  };

  // Remove a rate period
  const removeRatePeriod = (periodIndex) => {
    if (hotelForm.rate_periods.length > 1) {
      setHotelForm(prev => ({
        ...prev,
        rate_periods: prev.rate_periods.filter((_, i) => i !== periodIndex)
      }));
    }
  };

  // Update date range for a rate period
  const updateRatePeriodDate = (periodIndex, field, value) => {
    setHotelForm(prev => ({
      ...prev,
      rate_periods: prev.rate_periods.map((period, i) => 
        i === periodIndex ? { ...period, [field]: value } : period
      )
    }));
  };

  // Toggle day selection for a rate period
  const toggleRatePeriodDay = (periodIndex, day) => {
    setHotelForm(prev => ({
      ...prev,
      rate_periods: prev.rate_periods.map((period, i) => {
        if (i === periodIndex) {
          const currentDays = period.valid_days || [];
          const newDays = currentDays.includes(day)
            ? currentDays.filter(d => d !== day)
            : [...currentDays, day];
          return { ...period, valid_days: newDays };
        }
        return period;
      })
    }));
  };

  // Add room to a specific rate period
  const addRoomToPeriod = (periodIndex) => {
    setHotelForm(prev => ({
      ...prev,
      rate_periods: prev.rate_periods.map((period, i) => 
        i === periodIndex 
          ? { ...period, room_pricing: [...period.room_pricing, { room_category: "", ep: "", cp: "", map: "", ap: "" }] }
          : period
      )
    }));
  };

  // Remove room from a specific rate period
  const removeRoomFromPeriod = (periodIndex, roomIndex) => {
    setHotelForm(prev => ({
      ...prev,
      rate_periods: prev.rate_periods.map((period, i) => {
        if (i === periodIndex && period.room_pricing.length > 1) {
          return { ...period, room_pricing: period.room_pricing.filter((_, ri) => ri !== roomIndex) };
        }
        return period;
      })
    }));
  };

  // Update room pricing in a specific rate period
  const updateRoomInPeriod = (periodIndex, roomIndex, field, value) => {
    setHotelForm(prev => ({
      ...prev,
      rate_periods: prev.rate_periods.map((period, i) => 
        i === periodIndex 
          ? { 
              ...period, 
              room_pricing: period.room_pricing.map((room, ri) => 
                ri === roomIndex ? { ...room, [field]: value } : room
              )
            }
          : period
      )
    }));
  };

  // Update extra pricing (extra_bed, cwb, cnb) in a specific rate period
  const updateExtraPricingInPeriod = (periodIndex, type, field, value) => {
    setHotelForm(prev => ({
      ...prev,
      rate_periods: prev.rate_periods.map((period, i) => 
        i === periodIndex 
          ? { ...period, [type]: { ...period[type], [field]: value } }
          : period
      )
    }));
  };

  const submitHotel = async () => {
    if (!hotelForm.name || !hotelForm.destination) {
      toast.error("Please enter hotel name and destination");
      return;
    }
    try {
      // Parse numeric values for pricing in each rate period
      const payload = {
        name: hotelForm.name,
        destination: hotelForm.destination,
        city: hotelForm.city,
        category: hotelForm.category,
        rate_periods: hotelForm.rate_periods.map(period => ({
          from_date: period.from_date,
          to_date: period.to_date,
          valid_days: period.valid_days || ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
          room_pricing: period.room_pricing
            .filter(room => room.room_category)
            .map(room => ({
              room_category: room.room_category,
              ep: parseFloat(room.ep) || 0,
              cp: parseFloat(room.cp) || 0,
              map: parseFloat(room.map) || 0,
              ap: parseFloat(room.ap) || 0
            })),
          extra_bed: {
            ep: parseFloat(period.extra_bed.ep) || 0,
            cp: parseFloat(period.extra_bed.cp) || 0,
            map: parseFloat(period.extra_bed.map) || 0,
            ap: parseFloat(period.extra_bed.ap) || 0
          },
          cwb: {
            ep: parseFloat(period.cwb.ep) || 0,
            cp: parseFloat(period.cwb.cp) || 0,
            map: parseFloat(period.cwb.map) || 0,
            ap: parseFloat(period.cwb.ap) || 0
          },
          cnb: {
            ep: parseFloat(period.cnb.ep) || 0,
            cp: parseFloat(period.cnb.cp) || 0,
            map: parseFloat(period.cnb.map) || 0,
            ap: parseFloat(period.cnb.ap) || 0
          }
        })),
        amenities: hotelForm.amenities,
        status: hotelForm.status
      };
      if (editingItem) {
        await axios.put(`${API}/hotels/${editingItem.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Hotel updated!");
      } else {
        await axios.post(`${API}/hotels`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Hotel added!");
      }
      setShowHotelModal(false);
      resetHotelForm();
      fetchData();
    } catch (error) {
      console.error("Error saving hotel:", error);
      toast.error("Failed to save hotel");
    }
  };

  const openEditHotel = (item) => {
    setEditingItem(item);
    const defaultPeriod = {
      from_date: "",
      to_date: "",
      valid_days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      room_pricing: [{ room_category: "Deluxe Room", ep: "", cp: "", map: "", ap: "" }],
      extra_bed: { ep: "", cp: "", map: "", ap: "" },
      cwb: { ep: "", cp: "", map: "", ap: "" },
      cnb: { ep: "", cp: "", map: "", ap: "" }
    };
    
    setHotelForm({
      name: item.name || "",
      destination: item.destination || "",
      city: item.city || "",
      category: item.category || "3 Star",
      rate_periods: item.rate_periods?.length > 0 
        ? item.rate_periods.map(period => ({
            from_date: period.from_date || "",
            to_date: period.to_date || "",
            valid_days: period.valid_days || ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
            room_pricing: period.room_pricing?.length > 0 
              ? period.room_pricing.map(r => ({
                  room_category: r.room_category || "",
                  ep: r.ep || "",
                  cp: r.cp || "",
                  map: r.map || "",
                  ap: r.ap || ""
                }))
              : [{ room_category: "Deluxe Room", ep: "", cp: "", map: "", ap: "" }],
            extra_bed: period.extra_bed || { ep: "", cp: "", map: "", ap: "" },
            cwb: period.cwb || { ep: "", cp: "", map: "", ap: "" },
            cnb: period.cnb || { ep: "", cp: "", map: "", ap: "" }
          }))
        : [defaultPeriod],
      amenities: item.amenities || "",
      status: item.status || "active"
    });
    setShowHotelModal(true);
  };

  const toggleHotelDestinationExpand = (dest) => {
    setExpandedHotelDestinations(prev => ({ ...prev, [dest]: !prev[dest] }));
  };

  // Group hotels by destination
  const getGroupedHotels = () => {
    const filtered = hotels.filter(item =>
      searchTerm === "" || 
      item.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    return filtered.reduce((acc, item) => {
      const dest = item.destination;
      if (!acc[dest]) acc[dest] = [];
      acc[dest].push(item);
      return acc;
    }, {});
  };

  const groupedHotels = getGroupedHotels();

  return (
    <DashboardLayout type="admin">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-1">Resource Manager</h1>
                <p className="text-gray-500">Manage Destinations, Sightseeing, Hotels, and Transfers</p>
            </div>
            {activeTab === 'sightseeing' ? (
                <Button onClick={() => { setEditingItem(null); setSsForm({
                    destination: "", tour_name: "", duration: "", category: "",
                    description: "", price_adult: "", price_child: "", type: "Private",
                    region_type: sightseeingRegion, inclusions: "", exclusions: "", notes: "", image: "",
                    kms: ""
                }); setShowModal(true); }} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" /> Add Sightseeing
                </Button>
            ) : activeTab === 'destinations' ? (
                <Button onClick={() => { setEditingItem(null); setDestForm({ country: destinationRegion === 'international' ? 'International' : 'Domestic', name: "", cities: "", region_type: destinationRegion, status: "active" }); setShowDestModal(true); }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-destination-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Destination
                </Button>
            ) : activeTab === 'taxi' ? (
                <Button onClick={() => { resetTaxiForm(); setShowTaxiModal(true); }} className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="w-4 h-4 mr-2" /> Add Taxi Pricing
                </Button>
            ) : activeTab === 'entrytickets' ? (
                <Button onClick={() => { resetEntryTicketForm(); setShowEntryTicketModal(true); }} className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="w-4 h-4 mr-2" /> Add Entry Ticket
                </Button>
            ) : activeTab === 'templates' ? (
                <Button onClick={() => { resetTemplateForm(); setShowTemplateModal(true); }} className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="w-4 h-4 mr-2" /> Add Template
                </Button>
            ) : activeTab === 'hotels' ? (
                <Button onClick={() => { resetHotelForm(); setShowHotelModal(true); }} className="bg-orange-600 hover:bg-orange-700">
                    <Plus className="w-4 h-4 mr-2" /> Add Hotel
                </Button>
            ) : null}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <div className="flex justify-between items-center">
                <TabsList className="bg-gray-50">
                    <TabsTrigger value="destinations" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                        <Globe className="w-4 h-4 mr-2" /> Destinations
                    </TabsTrigger>
                    <TabsTrigger value="sightseeing" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                        <Camera className="w-4 h-4 mr-2" /> Sightseeing
                    </TabsTrigger>
                    <TabsTrigger value="hotels" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                        <Hotel className="w-4 h-4 mr-2" /> Hotels
                    </TabsTrigger>
                    <TabsTrigger value="taxi" className="data-[state=active]:bg-primary-700 data-[state=active]:text-white">
                        <Car className="w-4 h-4 mr-2" /> Taxi Pricing
                    </TabsTrigger>
                    <TabsTrigger value="entrytickets" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                        <Star className="w-4 h-4 mr-2" /> Entry Tickets
                    </TabsTrigger>
                    <TabsTrigger value="templates" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                        <FileText className="w-4 h-4 mr-2" /> Inc & Exc
                    </TabsTrigger>
                </TabsList>
                
                {(activeTab === 'sightseeing' || activeTab === 'taxi' || activeTab === 'entrytickets' || activeTab === 'templates' || activeTab === 'hotels') && (
                    <form onSubmit={handleSearch} className="flex gap-2">
                        <Input 
                            placeholder="Search destination..." 
                            className="bg-white border-gray-200 w-64 text-gray-800" 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <Button variant="outline" className="border-gray-200 text-gray-500"><Search className="w-4 h-4" /></Button>
                    </form>
                )}
            </div>

            <TabsContent value="destinations" className="space-y-4">
                {/* Domestic / International Sub-tabs */}
                <div className="flex items-center gap-4 border-b border-gray-200 pb-4" data-testid="destinations-region-tabs">
                  <button
                    onClick={() => setDestinationRegion("domestic")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      destinationRegion === "domestic"
                        ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                    data-testid="destinations-tab-domestic"
                  >
                    <Map className="w-4 h-4" />
                    Domestic
                    <Badge className="ml-1 bg-emerald-600 text-white text-xs">
                      {destinations.filter(d => (d.region_type || ((d.country || '').toLowerCase() === 'international' ? 'international' : 'domestic')) === 'domestic').length}
                    </Badge>
                  </button>
                  <button
                    onClick={() => setDestinationRegion("international")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      destinationRegion === "international"
                        ? "bg-blue-100 text-blue-700 border border-blue-300"
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                    data-testid="destinations-tab-international"
                  >
                    <Globe className="w-4 h-4" />
                    International
                    <Badge className="ml-1 bg-blue-600 text-white text-xs">
                      {destinations.filter(d => (d.region_type || ((d.country || '').toLowerCase() === 'international' ? 'international' : 'domestic')) === 'international').length}
                    </Badge>
                  </button>
                </div>

                <Card className="bg-white border-gray-200 shadow-sm">
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left">
                                <thead className="text-gray-500 bg-gray-50/50">
                                    <tr>
                                        <th className="px-4 py-3">Region</th>
                                        <th className="px-4 py-3">Destination (State/Region)</th>
                                        <th className="px-4 py-3">Cities</th>
                                        <th className="px-4 py-3">Status</th>
                                        <th className="px-4 py-3 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {(() => {
                                      const filtered = destinations
                                        .filter(d => (d.region_type || ((d.country || '').toLowerCase() === 'international' ? 'international' : 'domestic')) === destinationRegion)
                                        .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
                                      if (filtered.length === 0) {
                                        return (
                                          <tr>
                                            <td colSpan="5" className="px-4 py-8 text-center text-gray-400">
                                              No {destinationRegion} destinations added. Add one to start.
                                            </td>
                                          </tr>
                                        );
                                      }
                                      return filtered.map((dest) => (
                                        <tr key={dest.id} className="hover:bg-gray-50/30" data-testid={`destination-row-${dest.id}`}>
                                            <td className="px-4 py-3">
                                                <span className={`px-2 py-0.5 text-[11px] rounded-full font-semibold ${(dest.region_type || (dest.country || '').toLowerCase()) === 'international' ? 'bg-sky-100 text-sky-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                                    {(dest.region_type || (dest.country || 'domestic')).toString().replace(/^./, c => c.toUpperCase())}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-gray-800">{dest.name}</td>
                                            <td className="px-4 py-3 text-gray-600 max-w-xs truncate">{(dest.cities || []).join(", ")}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <Switch
                                                        checked={dest.status === 'active'}
                                                        onCheckedChange={() => toggleDestStatus(dest)}
                                                    />
                                                    <span className={dest.status === 'active' ? 'text-green-400' : 'text-gray-400'}>
                                                        {dest.status === 'active' ? 'Active' : 'Off'}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button variant="ghost" size="sm" onClick={() => openEditDest(dest)} data-testid={`edit-destination-${dest.id}`}>
                                                        <Edit className="w-4 h-4 text-gray-500" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(dest.id, 'destinations')} data-testid={`delete-destination-${dest.id}`}>
                                                        <Trash2 className="w-4 h-4 text-red-400" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                      ));
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </TabsContent>

            <TabsContent value="sightseeing" className="space-y-4">
                {/* Domestic/International Sub-tabs */}
                <div className="flex items-center gap-4 border-b border-gray-200 pb-4">
                  <button
                    onClick={() => setSightseeingRegion("domestic")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      sightseeingRegion === "domestic" 
                        ? "bg-emerald-100 text-emerald-700 border border-emerald-300" 
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <Map className="w-4 h-4" />
                    Domestic
                    <Badge className="ml-1 bg-emerald-600 text-white text-xs">
                      {sightseeing.filter(s => (s.region_type || "domestic") === "domestic").length}
                    </Badge>
                  </button>
                  <button
                    onClick={() => setSightseeingRegion("international")}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                      sightseeingRegion === "international" 
                        ? "bg-blue-100 text-blue-700 border border-blue-300" 
                        : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <Globe className="w-4 h-4" />
                    International
                    <Badge className="ml-1 bg-blue-600 text-white text-xs">
                      {sightseeing.filter(s => s.region_type === "international").length}
                    </Badge>
                  </button>
                </div>

                {loading ? (
                    <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700 mx-auto" /></div>
                ) : Object.keys(groupedSightseeing).length === 0 ? (
                    <Card className="bg-white border-gray-200">
                      <CardContent className="flex flex-col items-center justify-center py-12">
                        <Camera className="w-12 h-12 text-gray-300 mb-4" />
                        <p className="text-gray-500">No {sightseeingRegion} sightseeing tours found</p>
                        <Button onClick={() => { setEditingItem(null); setSsForm({
                          destination: "", tour_name: "", duration: "", category: "",
                          description: "", price_adult: "", price_child: "", type: "Private",
                          region_type: sightseeingRegion, inclusions: "", exclusions: "", notes: "", image: "",
                          kms: ""
                        }); setShowModal(true); }} variant="outline" className="mt-4">
                          Add First Tour
                        </Button>
                      </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-4">
                      {Object.keys(groupedSightseeing).sort().map(destination => (
                        <Card key={destination} className="bg-white border-gray-200 shadow-sm overflow-hidden">
                          <CardHeader 
                            className={`cursor-pointer py-3 ${
                              sightseeingRegion === "domestic" 
                                ? "bg-gradient-to-r from-emerald-50 to-teal-50" 
                                : "bg-gradient-to-r from-blue-50 to-indigo-50"
                            }`}
                            onClick={() => toggleDestinationExpand(destination)}
                          >
                            <div className="flex items-center justify-between">
                              <CardTitle className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${
                                  sightseeingRegion === "domestic" ? "bg-emerald-100" : "bg-blue-100"
                                }`}>
                                  <MapPin className={`w-5 h-5 ${
                                    sightseeingRegion === "domestic" ? "text-emerald-600" : "text-blue-600"
                                  }`} />
                                </div>
                                <div>
                                  <span className="text-lg font-bold text-gray-900">{destination}</span>
                                  <span className="ml-3 text-sm text-gray-500">
                                    {groupedSightseeing[destination].length} tour{groupedSightseeing[destination].length > 1 ? 's' : ''}
                                  </span>
                                </div>
                              </CardTitle>
                              {expandedDestinations[destination] ? (
                                <ChevronUp className="w-5 h-5 text-gray-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                          </CardHeader>
                          
                          {expandedDestinations[destination] && (
                            <CardContent className="p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {groupedSightseeing[destination].map(item => (
                                  <div key={item.id} className="bg-gray-50 rounded-lg border border-gray-100 hover:border-emerald-300 transition-colors group overflow-hidden">
                                    <div className="relative h-32 bg-gray-100">
                                      {item.image ? (
                                        <img src={item.image} alt={item.tour_name} className="w-full h-full object-cover" />
                                      ) : (
                                        <div className="flex items-center justify-center h-full text-gray-300">
                                          <ImageIcon className="w-8 h-8" />
                                        </div>
                                      )}
                                      <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                                        {item.category}
                                      </div>
                                    </div>
                                    <div className="p-3">
                                      <h4 className="text-gray-800 font-semibold text-sm mb-1 truncate">{item.tour_name}</h4>
                                      <div className="flex items-center text-gray-500 text-xs mb-2">
                                        <Clock className="w-3 h-3 mr-1" /> {item.duration}
                                        <span className="mx-2">•</span>
                                        <Badge variant="outline" className="text-xs">{item.type}</Badge>
                                      </div>
                                      <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                                        <div>
                                          <p className="text-xs text-gray-500">Net Cost</p>
                                          <p className="text-gray-800 text-sm font-mono">₹{item.price_adult} / ₹{item.price_child}</p>
                                        </div>
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                          <Button variant="ghost" size="sm" onClick={() => openEditSightseeing(item)} className="h-7 w-7 p-0 text-gray-500 hover:text-gray-900">
                                            <Edit className="w-3 h-3" />
                                          </Button>
                                          <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id, 'sightseeing')} className="h-7 w-7 p-0 text-red-400 hover:text-red-500">
                                            <Trash2 className="w-3 h-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </CardContent>
                          )}
                        </Card>
                      ))}
                    </div>
                )}
            </TabsContent>
            
            {/* HOTELS TAB */}
            <TabsContent value="hotels" className="space-y-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto" />
                </div>
              ) : Object.keys(groupedHotels).length === 0 ? (
                <Card className="bg-white border-gray-200">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Hotel className="w-12 h-12 text-gray-300 mb-4" />
                    <p className="text-gray-500">No hotels found</p>
                    <Button onClick={() => { resetHotelForm(); setShowHotelModal(true); }} variant="outline" className="mt-4">
                      Add First Hotel
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {Object.keys(groupedHotels).sort().map(destination => (
                    <Card key={destination} className="bg-white border-gray-200 shadow-sm overflow-hidden">
                      <CardHeader 
                        className="cursor-pointer py-3 bg-gradient-to-r from-orange-50 to-amber-50"
                        onClick={() => toggleHotelDestinationExpand(destination)}
                      >
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-orange-100">
                              <MapPin className="w-5 h-5 text-orange-600" />
                            </div>
                            <span className="text-lg font-bold text-gray-900">{destination}</span>
                            <Badge className="ml-2 bg-orange-600 text-white">
                              {groupedHotels[destination].length} Hotels
                            </Badge>
                          </CardTitle>
                          {expandedHotelDestinations[destination] ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </CardHeader>
                      
                      {expandedHotelDestinations[destination] && (
                        <CardContent className="p-4">
                          <div className="space-y-3">
                            {groupedHotels[destination].map(hotel => (
                              <div key={hotel.id} className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start gap-4">
                                    <div className="p-2 bg-orange-100 rounded-lg">
                                      <Hotel className="w-5 h-5 text-orange-600" />
                                    </div>
                                    <div>
                                      <h4 className="font-semibold text-gray-900">
                                        {hotel.name}
                                        {hotel.city && <span className="text-gray-500 font-normal ml-2">- {hotel.city}</span>}
                                      </h4>
                                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                                        <span className="flex items-center gap-1">
                                          <Star className="w-3 h-3 text-amber-500" />
                                          {hotel.category}
                                        </span>
                                        {hotel.rate_periods?.length > 0 && (
                                          <>
                                            <span>•</span>
                                            <span className="text-blue-600">
                                              {hotel.rate_periods.length} Rate Period{hotel.rate_periods.length > 1 ? 's' : ''}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                      {/* Room pricing summary from first rate period */}
                                      {hotel.rate_periods?.[0]?.room_pricing?.length > 0 && (
                                        <div className="mt-2 text-xs">
                                          <span className="text-gray-500">Rooms: </span>
                                          {hotel.rate_periods[0].room_pricing.map((room, idx) => (
                                            <span key={idx} className="inline-block mr-2">
                                              <Badge variant="outline" className="text-xs">
                                                {room.room_category}
                                                {room.cp > 0 && <span className="ml-1 text-emerald-600">₹{room.cp}</span>}
                                              </Badge>
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button variant="ghost" size="sm" onClick={() => openEditHotel(hotel)} className="text-orange-600 hover:text-orange-700">
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDelete(hotel.id, 'hotels')} className="text-red-500 hover:text-red-600">
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Taxi Pricing Tab */}
            <TabsContent value="taxi" className="space-y-4">
              {loading ? (
                <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" /></div>
              ) : filteredTaxiDestinations.length === 0 ? (
                <Card className="bg-white border-gray-200">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Car className="w-12 h-12 text-gray-300 mb-4" />
                    <p className="text-gray-500">No taxi pricing found</p>
                    <Button onClick={() => { resetTaxiForm(); setShowTaxiModal(true); }} variant="outline" className="mt-4">
                      Add First Pricing
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {filteredTaxiDestinations.map(destination => (
                    <Card key={destination} className="bg-white border-gray-200 shadow-sm overflow-hidden">
                      <CardHeader 
                        className="bg-gradient-to-r from-emerald-50 to-teal-50 cursor-pointer py-3"
                        onClick={() => toggleRow(destination)}
                      >
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-3">
                            <div className="p-2 bg-emerald-100 rounded-lg">
                              <MapPin className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div>
                              <span className="text-lg font-bold text-gray-900">{destination}</span>
                              <span className="ml-3 text-sm text-gray-500">
                                {groupedTaxiPricing[destination].length} pricing option{groupedTaxiPricing[destination].length > 1 ? 's' : ''}
                              </span>
                            </div>
                          </CardTitle>
                          {expandedRows[destination] ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </CardHeader>
                      
                      {expandedRows[destination] && (
                        <CardContent className="p-0">
                          <div className="divide-y divide-gray-100">
                            {groupedTaxiPricing[destination]
                              .sort((a, b) => a.nights - b.nights)
                              .map(item => (
                              <div key={item.id} className="p-4 hover:bg-gray-50">
                                <div className="flex items-center justify-between mb-3">
                                  <Badge className="bg-blue-100 text-blue-700 flex items-center gap-1">
                                    <Moon className="w-3 h-3" />
                                    {item.nights} Night{item.nights > 1 ? 's' : ''}
                                  </Badge>
                                  <div className="flex gap-2">
                                    <Button variant="ghost" size="sm" onClick={() => handleEditTaxi(item)}>
                                      <Edit className="w-4 h-4 text-blue-500" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteTaxi(item.id)}>
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </div>
                                
                                {/* Vehicle Prices Grid */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                                  {item.vehicles?.map((vehicle, idx) => (
                                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                                      <div className="flex items-center gap-2 mb-1">
                                        <Car className="w-4 h-4 text-gray-400" />
                                        <span className="text-sm font-medium text-gray-700 truncate">{vehicle.vehicle_type}</span>
                                      </div>
                                      <p className="text-lg font-bold text-emerald-600">{formatCurrency(vehicle.price)}</p>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ENTRY TICKETS TAB */}
            <TabsContent value="entrytickets" className="space-y-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700 mx-auto" />
                </div>
              ) : filteredEntryDestinations.length === 0 ? (
                <Card className="bg-white border-gray-200">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <Star className="w-12 h-12 text-gray-300 mb-4" />
                    <p className="text-gray-500">No entry tickets found for international destinations</p>
                    <Button onClick={() => { resetEntryTicketForm(); setShowEntryTicketModal(true); }} variant="outline" className="mt-4">
                      Add First Entry Ticket
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {/* Info Banner */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-blue-700 text-sm">
                      <Globe className="w-4 h-4 inline mr-2" />
                      <strong>International Entry Tickets</strong> - Manage attraction tickets for international destinations like Dubai, Singapore, Europe, etc.
                    </p>
                  </div>

                  {filteredEntryDestinations.sort().map(destination => (
                    <Card key={destination} className="bg-white border-gray-200 shadow-sm overflow-hidden">
                      <CardHeader 
                        className="cursor-pointer py-3 bg-gradient-to-r from-blue-50 to-indigo-50"
                        onClick={() => toggleEntryDestinationExpand(destination)}
                      >
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-100">
                              <Globe className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <span className="text-lg font-bold text-gray-900">{destination}</span>
                              <span className="ml-3 text-sm text-gray-500">
                                {groupedEntryTickets[destination].length} ticket{groupedEntryTickets[destination].length > 1 ? 's' : ''}
                              </span>
                            </div>
                          </CardTitle>
                          {expandedEntryDestinations[destination] ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </CardHeader>
                      
                      {expandedEntryDestinations[destination] && (
                        <CardContent className="p-4">
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="text-left py-3 px-4 font-medium text-gray-600 text-sm">Attraction</th>
                                  <th className="text-left py-3 px-4 font-medium text-gray-600 text-sm">Type</th>
                                  <th className="text-right py-3 px-4 font-medium text-gray-600 text-sm">Adult</th>
                                  <th className="text-right py-3 px-4 font-medium text-blue-600 text-sm">CWB</th>
                                  <th className="text-right py-3 px-4 font-medium text-amber-600 text-sm">CNB</th>
                                  <th className="text-center py-3 px-4 font-medium text-gray-600 text-sm">Validity</th>
                                  <th className="text-center py-3 px-4 font-medium text-gray-600 text-sm">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {groupedEntryTickets[destination].map(ticket => (
                                  <tr key={ticket.id} className="hover:bg-gray-50/50">
                                    <td className="py-3 px-4">
                                      <p className="font-medium text-gray-800">{ticket.attraction_name}</p>
                                      {ticket.description && (
                                        <p className="text-xs text-gray-500 mt-1 truncate max-w-xs">{ticket.description}</p>
                                      )}
                                    </td>
                                    <td className="py-3 px-4">
                                      <Badge variant="outline" className="text-xs">{ticket.ticket_type}</Badge>
                                    </td>
                                    <td className="py-3 px-4 text-right font-mono text-gray-800">
                                      {ticket.currency} {ticket.price_adult}
                                    </td>
                                    <td className="py-3 px-4 text-right font-mono text-blue-600">
                                      {ticket.currency} {ticket.price_cwb}
                                    </td>
                                    <td className="py-3 px-4 text-right font-mono text-amber-600">
                                      {ticket.currency} {ticket.price_cnb}
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                      <span className="text-xs text-gray-500">{ticket.validity}</span>
                                    </td>
                                    <td className="py-3 px-4">
                                      <div className="flex items-center justify-center gap-1">
                                        <Button variant="ghost" size="sm" onClick={() => handleEditEntryTicket(ticket)} className="h-8 w-8 p-0 text-blue-500 hover:text-blue-700">
                                          <Edit className="w-4 h-4" />
                                        </Button>
                                        <Button variant="ghost" size="sm" onClick={() => handleDeleteEntryTicket(ticket.id)} className="h-8 w-8 p-0 text-red-400 hover:text-red-600">
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* INCLUSIONS & EXCLUSIONS TEMPLATES TAB */}
            <TabsContent value="templates" className="space-y-4">
              {/* Domestic/International Sub-tabs */}
              <div className="flex items-center gap-4 border-b border-gray-200 pb-4">
                <button
                  onClick={() => setPackageTemplateRegion("domestic")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    packageTemplateRegion === "domestic" 
                      ? "bg-emerald-100 text-emerald-700 border border-emerald-300" 
                      : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Map className="w-4 h-4" />
                  Domestic
                  <Badge className="ml-1 bg-emerald-600 text-white text-xs">
                    {packageTemplates.filter(t => (t.region_type || "domestic") === "domestic").length}
                  </Badge>
                </button>
                <button
                  onClick={() => setPackageTemplateRegion("international")}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                    packageTemplateRegion === "international" 
                      ? "bg-blue-100 text-blue-700 border border-blue-300" 
                      : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <Globe className="w-4 h-4" />
                  International
                  <Badge className="ml-1 bg-blue-600 text-white text-xs">
                    {packageTemplates.filter(t => t.region_type === "international").length}
                  </Badge>
                </button>
              </div>

              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto" />
                </div>
              ) : Object.keys(groupedTemplates).length === 0 ? (
                <Card className="bg-white border-gray-200">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <FileText className="w-12 h-12 text-gray-300 mb-4" />
                    <p className="text-gray-500">No {packageTemplateRegion} templates found</p>
                    <Button 
                      onClick={() => { 
                        resetTemplateForm(); 
                        setTemplateForm(prev => ({ ...prev, region_type: packageTemplateRegion }));
                        setShowTemplateModal(true); 
                      }} 
                      variant="outline" 
                      className="mt-4"
                    >
                      Add First Template
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {Object.keys(groupedTemplates).sort().map(destination => (
                    <Card key={destination} className="bg-white border-gray-200 shadow-sm overflow-hidden">
                      <CardHeader 
                        className={`cursor-pointer py-3 ${
                          packageTemplateRegion === "domestic" 
                            ? "bg-gradient-to-r from-emerald-50 to-teal-50" 
                            : "bg-gradient-to-r from-blue-50 to-indigo-50"
                        }`}
                        onClick={() => toggleTemplateDestinationExpand(destination)}
                      >
                        <div className="flex items-center justify-between">
                          <CardTitle className="flex items-center gap-3">
                            <div className={`p-2 rounded-lg ${
                              packageTemplateRegion === "domestic" ? "bg-emerald-100" : "bg-blue-100"
                            }`}>
                              <MapPin className={`w-5 h-5 ${
                                packageTemplateRegion === "domestic" ? "text-emerald-600" : "text-blue-600"
                              }`} />
                            </div>
                            <span className="text-lg font-bold text-gray-900">{destination}</span>
                          </CardTitle>
                          {expandedTemplateDestinations[destination] ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </CardHeader>
                      
                      {expandedTemplateDestinations[destination] && (
                        <CardContent className="p-4">
                          {groupedTemplates[destination].map(item => (
                            <div key={item.id} className="space-y-4">
                              <div className="flex justify-end gap-2 mb-2">
                                <Button variant="ghost" size="sm" onClick={() => openEditTemplate(item)} className="text-purple-600 hover:text-purple-700">
                                  <Edit className="w-4 h-4 mr-1" /> Edit
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(item.id)} className="text-red-500 hover:text-red-600">
                                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                                </Button>
                              </div>
                              
                              <div className="grid md:grid-cols-2 gap-4">
                                {/* Inclusions Card */}
                                <div className="bg-emerald-50/50 rounded-lg border border-emerald-200 p-4">
                                  <div className="flex items-center gap-2 mb-3">
                                    <CheckSquare className="w-5 h-5 text-emerald-600" />
                                    <h4 className="font-semibold text-emerald-700">Inclusions</h4>
                                  </div>
                                  <div className="text-gray-700 text-sm whitespace-pre-wrap bg-white p-3 rounded border border-emerald-100 min-h-[100px]">
                                    {item.inclusions || <span className="text-gray-400 italic">No inclusions added</span>}
                                  </div>
                                </div>
                                
                                {/* Exclusions Card */}
                                <div className="bg-red-50/50 rounded-lg border border-red-200 p-4">
                                  <div className="flex items-center gap-2 mb-3">
                                    <XSquare className="w-5 h-5 text-red-600" />
                                    <h4 className="font-semibold text-red-700">Exclusions</h4>
                                  </div>
                                  <div className="text-gray-700 text-sm whitespace-pre-wrap bg-white p-3 rounded border border-red-100 min-h-[100px]">
                                    {item.exclusions || <span className="text-gray-400 italic">No exclusions added</span>}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
        </Tabs>

        {/* Add Sightseeing Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-2xl">
                <DialogHeader><DialogTitle>{editingItem ? 'Edit' : 'Add'} Sightseeing Tour</DialogTitle></DialogHeader>
                <form onSubmit={submitSightseeing} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                    {/* Region Type Selection */}
                    <div className="flex gap-4 p-3 bg-gray-50 rounded-lg">
                      <Label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="region_type"
                          value="domestic"
                          checked={ssForm.region_type === "domestic"}
                          onChange={(e) => setSsForm({...ssForm, region_type: e.target.value})}
                          className="w-4 h-4 text-emerald-600"
                        />
                        <Map className="w-4 h-4 text-emerald-600" />
                        <span className="text-gray-700">Domestic</span>
                      </Label>
                      <Label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="region_type"
                          value="international"
                          checked={ssForm.region_type === "international"}
                          onChange={(e) => setSsForm({...ssForm, region_type: e.target.value})}
                          className="w-4 h-4 text-blue-600"
                        />
                        <Globe className="w-4 h-4 text-blue-600" />
                        <span className="text-gray-700">International</span>
                      </Label>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>Tour Name</Label><Input value={ssForm.tour_name} onChange={e => setSsForm({...ssForm, tour_name: e.target.value})} className="bg-gray-50 border-gray-200" required /></div>
                        <div>
                            <Label>Destination</Label>
                            <select
                              value={ssForm.destination}
                              onChange={e => setSsForm({...ssForm, destination: e.target.value})}
                              className="w-full mt-1 h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm"
                              required
                              data-testid="ss-destination-select"
                            >
                              <option value="">Select {ssForm.region_type === "international" ? "International" : "Domestic"} Destination</option>
                              {destinations
                                .filter(d => d.status === 'active' && ((ssForm.region_type === "international") ? d.country === 'International' : d.country !== 'International'))
                                .map(d => (
                                  <option key={d.id} value={d.name}>{d.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {/* ... rest of form ... */}
                    <div>
                        <Label>Tour Image</Label>
                        <div className="flex items-center gap-4 mt-1">
                            {ssForm.image && (
                                <img src={ssForm.image} alt="Preview" className="w-16 h-16 object-cover rounded border border-gray-200" />
                            )}
                            <div className="relative">
                                <Button type="button" variant="outline" className="border-gray-200 text-gray-600">
                                    <Camera className="w-4 h-4 mr-2" /> Upload Image
                                </Button>
                                <input 
                                    type="file" 
                                    accept="image/*" 
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                    onChange={handleImageUpload}
                                />
                            </div>
                            {ssForm.image && (
                                <Button type="button" variant="ghost" size="sm" onClick={() => setSsForm({...ssForm, image: ""})} className="text-red-400">
                                    <X className="w-4 h-4" />
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div><Label>Duration</Label><Input value={ssForm.duration} onChange={e => setSsForm({...ssForm, duration: e.target.value})} className="bg-gray-50 border-gray-200" placeholder="e.g. 4 Hours" /></div>
                        {ssForm.region_type === 'domestic' && (
                          <div>
                            <Label>KMS (Distance)</Label>
                            <Input value={ssForm.kms} onChange={e => setSsForm({...ssForm, kms: e.target.value})} className="bg-gray-50 border-gray-200" placeholder="e.g. 150 KMS" />
                          </div>
                        )}
                    </div>
                    
                    {/* Adult/Child Price only for International */}
                    {ssForm.region_type === 'international' && (
                      <div className="grid grid-cols-3 gap-4">
                        <div><Label>Adult Price</Label><Input type="number" value={ssForm.price_adult} onChange={e => setSsForm({...ssForm, price_adult: e.target.value})} className="bg-gray-50 border-gray-200" /></div>
                        <div><Label>Child Price</Label><Input type="number" value={ssForm.price_child} onChange={e => setSsForm({...ssForm, price_child: e.target.value})} className="bg-gray-50 border-gray-200" /></div>
                        <div>
                            <Label>Type</Label>
                            <Select value={ssForm.type} onValueChange={v => setSsForm({...ssForm, type: v})}>
                                <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Private">Private</SelectItem>
                                    <SelectItem value="SIC">SIC</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                      </div>
                    )}
                    
                    {/* Type field for Domestic */}
                    {ssForm.region_type === 'domestic' && (
                      <div className="w-1/3">
                        <Label>Type</Label>
                        <Select value={ssForm.type} onValueChange={v => setSsForm({...ssForm, type: v})}>
                            <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="Private">Private</SelectItem>
                                <SelectItem value="SIC">SIC</SelectItem>
                            </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    <div><Label>Description</Label><Textarea value={ssForm.description} onChange={e => setSsForm({...ssForm, description: e.target.value})} className="bg-gray-50 border-gray-200" /></div>
                    <Button type="submit" className="w-full bg-primary-700" disabled={savingSightseeing} data-testid="submit-sightseeing-btn">
                        {savingSightseeing ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                        ) : (
                            editingItem ? "Update Tour" : "Save Tour"
                        )}
                    </Button>
                </form>
            </DialogContent>
        </Dialog>

        {/* Add Destination Modal */}
        <Dialog open={showDestModal} onOpenChange={setShowDestModal}>
            <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-lg">
                <DialogHeader><DialogTitle>{editingItem ? 'Edit' : 'Add'} Destination</DialogTitle></DialogHeader>
                <form onSubmit={submitDestination} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Region</Label>
                            <Select value={destForm.region_type} onValueChange={v => setDestForm({...destForm, region_type: v, country: v === 'international' ? 'International' : 'Domestic'})}>
                                <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="dest-region-select">
                                    <SelectValue placeholder="Select Region" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="domestic">Domestic</SelectItem>
                                    <SelectItem value="international">International</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div><Label>Destination (State/Region)</Label><Input value={destForm.name} onChange={e => setDestForm({...destForm, name: e.target.value})} className="bg-gray-50 border-gray-200" required placeholder="e.g. Kerala" /></div>
                    </div>
                    <div>
                        <Label>Cities (comma separated)</Label>
                        <Textarea 
                            value={destForm.cities} 
                            onChange={e => setDestForm({...destForm, cities: e.target.value})} 
                            className="bg-gray-50 border-gray-200" 
                            placeholder="Munnar, Thekkady, Alleppey, Cochin"
                        />
                    </div>
                    {/* Active toggle handled in table for simplicity, but can be here too */}
                    <Button type="submit" className="w-full bg-primary-700">Save Destination</Button>
                </form>
            </DialogContent>
        </Dialog>

        {/* Add Taxi Pricing Modal */}
        <Dialog open={showTaxiModal} onOpenChange={setShowTaxiModal}>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit' : 'Add'} Taxi Pricing</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Destination & Nights */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Destination *</Label>
                  <Select value={taxiForm.destination} onValueChange={(val) => setTaxiForm({ ...taxiForm, destination: val })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select destination" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Domestic</SelectLabel>
                        {destinations.filter(d => d.country !== 'International').map(d => (
                          <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                        ))}
                      </SelectGroup>
                      <SelectGroup>
                        <SelectLabel>International</SelectLabel>
                        {destinations.filter(d => d.country === 'International').map(d => (
                          <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <Input
                    className="mt-2"
                    placeholder="Or type new destination"
                    value={taxiForm.destination}
                    onChange={(e) => setTaxiForm({ ...taxiForm, destination: e.target.value })}
                  />
                </div>
                <div>
                  <Label>No of Nights *</Label>
                  <Input
                    type="number"
                    min="1"
                    value={taxiForm.nights}
                    onChange={(e) => setTaxiForm({ ...taxiForm, nights: parseInt(e.target.value) || 1 })}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Vehicle Prices */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-base">Vehicle Types & Prices</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addVehicle}>
                    <Plus className="w-4 h-4 mr-1" /> Add Vehicle
                  </Button>
                </div>
                
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {taxiForm.vehicles.map((vehicle, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <Select 
                          value={vehicle.vehicle_type} 
                          onValueChange={(val) => updateVehicle(idx, 'vehicle_type', val)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {VEHICLE_TYPES.map(type => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-32">
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                          <Input
                            type="number"
                            min="0"
                            value={vehicle.price}
                            onChange={(e) => updateVehicle(idx, 'price', e.target.value)}
                            className="pl-7"
                            placeholder="Price"
                          />
                        </div>
                      </div>
                      {taxiForm.vehicles.length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeVehicle(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setShowTaxiModal(false)}>
                  Cancel
                </Button>
                <Button onClick={submitTaxiPricing} className="bg-emerald-600 hover:bg-emerald-700">
                  <Save className="w-4 h-4 mr-2" /> {editingItem ? 'Update' : 'Save'} Pricing
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Entry Ticket Modal */}
        <Dialog open={showEntryTicketModal} onOpenChange={setShowEntryTicketModal}>
          <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit' : 'Add'} Entry Ticket (International)</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Region *</Label>
                  <select
                    value={entryTicketForm.region_type}
                    onChange={e => setEntryTicketForm({...entryTicketForm, region_type: e.target.value, destination: ""})}
                    className="w-full mt-1 h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm"
                    data-testid="entry-ticket-region"
                  >
                    <option value="domestic">Domestic</option>
                    <option value="international">International</option>
                  </select>
                </div>
                <div>
                  <Label>Destination *</Label>
                  <select
                    value={entryTicketForm.destination}
                    onChange={e => setEntryTicketForm({...entryTicketForm, destination: e.target.value})}
                    className="w-full mt-1 h-10 rounded-md border border-gray-200 bg-gray-50 px-3 text-sm"
                    required
                    data-testid="entry-ticket-destination"
                  >
                    <option value="">Select {entryTicketForm.region_type === "international" ? "International" : "Domestic"} Destination</option>
                    {destinations
                      .filter(d => d.status === 'active' && ((entryTicketForm.region_type === "international") ? d.country === 'International' : d.country !== 'International'))
                      .map(d => (
                        <option key={d.id} value={d.name}>{d.name}</option>
                      ))}
                  </select>
                </div>
                <div>
                  <Label>Attraction Name *</Label>
                  <Input 
                    value={entryTicketForm.attraction_name} 
                    onChange={e => setEntryTicketForm({...entryTicketForm, attraction_name: e.target.value})} 
                    className="bg-gray-50 border-gray-200" 
                    placeholder="e.g. Burj Khalifa" 
                    required 
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Ticket Type</Label>
                  <Select value={entryTicketForm.ticket_type} onValueChange={v => setEntryTicketForm({...entryTicketForm, ticket_type: v})}>
                    <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Standard">Standard</SelectItem>
                      <SelectItem value="Premium">Premium</SelectItem>
                      <SelectItem value="VIP">VIP</SelectItem>
                      <SelectItem value="Fast Track">Fast Track</SelectItem>
                      <SelectItem value="Combo">Combo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Currency</Label>
                  <Select value={entryTicketForm.currency} onValueChange={v => setEntryTicketForm({...entryTicketForm, currency: v})}>
                    <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="INR">INR (₹)</SelectItem>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="AED">AED (د.إ)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                      <SelectItem value="SGD">SGD (S$)</SelectItem>
                      <SelectItem value="THB">THB (฿)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Validity</Label>
                  <Select value={entryTicketForm.validity} onValueChange={v => setEntryTicketForm({...entryTicketForm, validity: v})}>
                    <SelectTrigger className="bg-gray-50 border-gray-200"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single Entry">Single Entry</SelectItem>
                      <SelectItem value="Multi Entry">Multi Entry</SelectItem>
                      <SelectItem value="1 Day">1 Day</SelectItem>
                      <SelectItem value="2 Days">2 Days</SelectItem>
                      <SelectItem value="Flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Pricing Section */}
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <Label className="text-gray-700 font-medium mb-3 block">Pricing (Per Person)</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm text-gray-600">Adult Price</Label>
                    <Input 
                      type="number" 
                      value={entryTicketForm.price_adult} 
                      onChange={e => setEntryTicketForm({...entryTicketForm, price_adult: e.target.value})} 
                      className="bg-white border-gray-200" 
                      placeholder="0" 
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-blue-600">CWB Price</Label>
                    <Input 
                      type="number" 
                      value={entryTicketForm.price_cwb} 
                      onChange={e => setEntryTicketForm({...entryTicketForm, price_cwb: e.target.value})} 
                      className="bg-blue-50 border-blue-200" 
                      placeholder="0" 
                    />
                  </div>
                  <div>
                    <Label className="text-sm text-amber-600">CNB Price</Label>
                    <Input 
                      type="number" 
                      value={entryTicketForm.price_cnb} 
                      onChange={e => setEntryTicketForm({...entryTicketForm, price_cnb: e.target.value})} 
                      className="bg-amber-50 border-amber-200" 
                      placeholder="0" 
                    />
                  </div>
                </div>
              </div>

              {/* Rate Periods Section */}
              <div className="p-4 bg-emerald-50/30 rounded-lg border border-emerald-200">
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-gray-700 font-medium">Rate Periods & Valid Days</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addEntryTicketRatePeriod} data-testid="entry-ticket-add-period">
                    + Add Rate Period
                  </Button>
                </div>
                <div className="space-y-3">
                  {(entryTicketForm.rate_periods || []).map((period, pIdx) => (
                    <div key={pIdx} className="p-3 bg-white border border-emerald-100 rounded-md" data-testid={`entry-ticket-period-${pIdx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-emerald-700">Rate Period {pIdx + 1}</span>
                        {(entryTicketForm.rate_periods || []).length > 1 && (
                          <button type="button" onClick={() => removeEntryTicketRatePeriod(pIdx)} className="text-xs text-red-600 hover:underline">Remove</button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <Label className="text-xs text-gray-600">Start Date</Label>
                          <Input type="date" value={period.start_date} onChange={e => updateEntryTicketRatePeriod(pIdx, 'start_date', e.target.value)} className="bg-gray-50 border-gray-200" />
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600">End Date</Label>
                          <Input type="date" value={period.end_date} onChange={e => updateEntryTicketRatePeriod(pIdx, 'end_date', e.target.value)} className="bg-gray-50 border-gray-200" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600 mb-1.5 block">Valid Days</Label>
                        <div className="flex flex-wrap gap-1.5">
                          {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(day => {
                            const checked = (period.valid_days || []).includes(day);
                            return (
                              <button
                                key={day}
                                type="button"
                                onClick={() => toggleEntryTicketValidDay(pIdx, day)}
                                className={`px-3 py-1 rounded-md text-xs font-medium border transition ${checked ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-400'}`}
                                data-testid={`entry-ticket-day-${pIdx}-${day}`}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Textarea 
                  value={entryTicketForm.description} 
                  onChange={e => setEntryTicketForm({...entryTicketForm, description: e.target.value})} 
                  className="bg-gray-50 border-gray-200" 
                  placeholder="Brief description of the ticket..." 
                  rows={2}
                />
              </div>

              <div>
                <Label>Inclusions</Label>
                <Textarea 
                  value={entryTicketForm.inclusions} 
                  onChange={e => setEntryTicketForm({...entryTicketForm, inclusions: e.target.value})} 
                  className="bg-gray-50 border-gray-200" 
                  placeholder="What's included..." 
                  rows={2}
                />
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea 
                  value={entryTicketForm.notes} 
                  onChange={e => setEntryTicketForm({...entryTicketForm, notes: e.target.value})} 
                  className="bg-gray-50 border-gray-200" 
                  placeholder="Any additional notes..." 
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowEntryTicketModal(false); resetEntryTicketForm(); }}>
                  Cancel
                </Button>
                <Button onClick={submitEntryTicket} className="bg-blue-600 hover:bg-blue-700">
                  <Save className="w-4 h-4 mr-2" /> {editingItem ? 'Update' : 'Save'} Entry Ticket
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add/Edit Package Template Modal */}
        <Dialog open={showTemplateModal} onOpenChange={setShowTemplateModal}>
          <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                {editingItem ? 'Edit' : 'Add'} Inclusions & Exclusions Template
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              {/* Region Type Selection */}
              <div className="flex gap-4 p-3 bg-gray-50 rounded-lg">
                <Label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    checked={templateForm.region_type === "domestic"} 
                    onChange={() => setTemplateForm({...templateForm, region_type: "domestic"})} 
                    className="text-emerald-600"
                  />
                  <Map className="w-4 h-4 text-emerald-600" />
                  <span className="text-emerald-700 font-medium">Domestic</span>
                </Label>
                <Label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="radio" 
                    checked={templateForm.region_type === "international"} 
                    onChange={() => setTemplateForm({...templateForm, region_type: "international"})} 
                    className="text-blue-600"
                  />
                  <Globe className="w-4 h-4 text-blue-600" />
                  <span className="text-blue-700 font-medium">International</span>
                </Label>
              </div>

              <div>
                <Label>Destination *</Label>
                <Input 
                  value={templateForm.destination} 
                  onChange={e => setTemplateForm({...templateForm, destination: e.target.value})} 
                  className="bg-gray-50 border-gray-200" 
                  placeholder="e.g. Ladakh, Dubai, Switzerland..."
                  data-testid="template-destination-input"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2 text-emerald-700">
                  <CheckSquare className="w-4 h-4" />
                  Inclusions
                </Label>
                <Textarea 
                  value={templateForm.inclusions} 
                  onChange={e => setTemplateForm({...templateForm, inclusions: e.target.value})} 
                  className="bg-emerald-50/50 border-emerald-200" 
                  placeholder="• Accommodation on twin/double sharing basis
• Daily breakfast at the hotel
• All sightseeing as per itinerary
• Airport/Railway transfers
• All applicable taxes"
                  rows={6}
                  data-testid="template-inclusions-textarea"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2 text-red-600">
                  <XSquare className="w-4 h-4" />
                  Exclusions
                </Label>
                <Textarea 
                  value={templateForm.exclusions} 
                  onChange={e => setTemplateForm({...templateForm, exclusions: e.target.value})} 
                  className="bg-red-50/50 border-red-200" 
                  placeholder="• Airfare / Train fare
• Any meals not mentioned in itinerary
• Personal expenses (tips, laundry, phone calls)
• Travel insurance
• Entry fees to monuments (if applicable)
• Any item not mentioned in inclusions"
                  rows={6}
                  data-testid="template-exclusions-textarea"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowTemplateModal(false); resetTemplateForm(); }}>
                  Cancel
                </Button>
                <Button onClick={submitTemplate} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="w-4 h-4 mr-2" /> {editingItem ? 'Update' : 'Save'} Template
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add/Edit Hotel Modal */}
        <Dialog open={showHotelModal} onOpenChange={setShowHotelModal}>
          <DialogContent className="bg-white border-gray-200 text-gray-800 max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Hotel className="w-5 h-5 text-orange-600" />
                {editingItem ? 'Edit' : 'Add'} Hotel
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 max-h-[75vh] overflow-y-auto pr-2">
              {/* Basic Info Row */}
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <Label>Destination *</Label>
                  <Input 
                    value={hotelForm.destination} 
                    onChange={e => setHotelForm({...hotelForm, destination: e.target.value})} 
                    className="bg-gray-50 border-gray-200" 
                    placeholder="e.g. Kerala"
                    data-testid="hotel-destination-input"
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input 
                    value={hotelForm.city} 
                    onChange={e => setHotelForm({...hotelForm, city: e.target.value})} 
                    className="bg-gray-50 border-gray-200" 
                    placeholder="e.g. Munnar"
                    data-testid="hotel-city-input"
                  />
                </div>
                <div>
                  <Label>Hotel Name *</Label>
                  <Input 
                    value={hotelForm.name} 
                    onChange={e => setHotelForm({...hotelForm, name: e.target.value})} 
                    className="bg-gray-50 border-gray-200" 
                    placeholder="e.g. The Grand Palace"
                    data-testid="hotel-name-input"
                  />
                </div>
                <div>
                  <Label>Hotel Category</Label>
                  <Select value={hotelForm.category} onValueChange={v => setHotelForm({...hotelForm, category: v})}>
                    <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="hotel-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Budget">Budget</SelectItem>
                      <SelectItem value="3 Star">3 Star</SelectItem>
                      <SelectItem value="3 Star Deluxe">3 Star Deluxe</SelectItem>
                      <SelectItem value="4 Star">4 Star</SelectItem>
                      <SelectItem value="4 Star Deluxe">4 Star Deluxe</SelectItem>
                      <SelectItem value="5 Star">5 Star</SelectItem>
                      <SelectItem value="5 Star Luxury">5 Star Luxury</SelectItem>
                      <SelectItem value="Boutique">Boutique</SelectItem>
                      <SelectItem value="Heritage">Heritage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Rate Periods - Each contains date range + room pricing table */}
              {hotelForm.rate_periods.map((period, periodIdx) => (
                <div key={periodIdx} className="border-2 border-orange-200 rounded-lg overflow-hidden">
                  {/* Period Header with Date Range */}
                  <div className="bg-orange-50 px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <span className="font-semibold text-orange-800">Rate Period {periodIdx + 1}</span>
                      <div className="flex items-center gap-2">
                        <Input 
                          type="date"
                          value={period.from_date}
                          onChange={e => updateRatePeriodDate(periodIdx, 'from_date', e.target.value)}
                          className="bg-white border-gray-200 w-40"
                          data-testid={`period-${periodIdx}-from`}
                        />
                        <span className="text-gray-500">to</span>
                        <Input 
                          type="date"
                          value={period.to_date}
                          onChange={e => updateRatePeriodDate(periodIdx, 'to_date', e.target.value)}
                          className="bg-white border-gray-200 w-40"
                          data-testid={`period-${periodIdx}-to`}
                        />
                      </div>
                    </div>
                    {hotelForm.rate_periods.length > 1 && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeRatePeriod(periodIdx)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  {/* Valid Days Selection */}
                  <div className="px-4 py-3 bg-gray-50 border-t border-orange-100">
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium text-gray-700">Valid Days:</span>
                      <div className="flex gap-2">
                        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => {
                          const isSelected = (period.valid_days || []).includes(day);
                          return (
                            <button
                              key={day}
                              type="button"
                              onClick={() => toggleRatePeriodDay(periodIdx, day)}
                              className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                                isSelected 
                                  ? 'bg-orange-600 text-white border-orange-600' 
                                  : 'bg-white text-gray-500 border-gray-300 hover:border-orange-400'
                              }`}
                              data-testid={`period-${periodIdx}-day-${day.toLowerCase()}`}
                            >
                              {day}
                            </button>
                          );
                        })}
                      </div>
                      {(period.valid_days || []).length < 7 && (period.valid_days || []).length > 0 && (
                        <span className="text-xs text-amber-600 ml-2">
                          Rate not valid on: {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].filter(d => !(period.valid_days || []).includes(d)).join(", ")}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Room Pricing Table */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium text-gray-700">Room Category Pricing</Label>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm" 
                        onClick={() => addRoomToPeriod(periodIdx)}
                        className="text-orange-600 border-orange-300 hover:bg-orange-50 h-7 text-xs"
                      >
                        <Plus className="w-3 h-3 mr-1" /> Add Room
                      </Button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 border-b">
                          <tr>
                            <th className="text-left py-2 px-3 font-medium text-gray-700 min-w-[150px]">Room Category</th>
                            <th className="text-center py-2 px-2 font-medium text-gray-700 w-20">EP</th>
                            <th className="text-center py-2 px-2 font-medium text-gray-700 w-20">CP</th>
                            <th className="text-center py-2 px-2 font-medium text-gray-700 w-20">MAP</th>
                            <th className="text-center py-2 px-2 font-medium text-gray-700 w-20">AP</th>
                            <th className="w-10"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {period.room_pricing.map((room, roomIdx) => (
                            <tr key={roomIdx} className="hover:bg-gray-50">
                              <td className="py-1.5 px-2">
                                <Input 
                                  value={room.room_category}
                                  onChange={e => updateRoomInPeriod(periodIdx, roomIdx, 'room_category', e.target.value)}
                                  className="bg-white border-gray-200 h-8 text-sm"
                                  placeholder="e.g. Deluxe Room"
                                />
                              </td>
                              <td className="py-1.5 px-1">
                                <Input 
                                  type="number"
                                  value={room.ep}
                                  onChange={e => updateRoomInPeriod(periodIdx, roomIdx, 'ep', e.target.value)}
                                  className="bg-white border-gray-200 text-center h-8 text-sm"
                                  placeholder="0"
                                />
                              </td>
                              <td className="py-1.5 px-1">
                                <Input 
                                  type="number"
                                  value={room.cp}
                                  onChange={e => updateRoomInPeriod(periodIdx, roomIdx, 'cp', e.target.value)}
                                  className="bg-white border-gray-200 text-center h-8 text-sm"
                                  placeholder="0"
                                />
                              </td>
                              <td className="py-1.5 px-1">
                                <Input 
                                  type="number"
                                  value={room.map}
                                  onChange={e => updateRoomInPeriod(periodIdx, roomIdx, 'map', e.target.value)}
                                  className="bg-white border-gray-200 text-center h-8 text-sm"
                                  placeholder="0"
                                />
                              </td>
                              <td className="py-1.5 px-1">
                                <Input 
                                  type="number"
                                  value={room.ap}
                                  onChange={e => updateRoomInPeriod(periodIdx, roomIdx, 'ap', e.target.value)}
                                  className="bg-white border-gray-200 text-center h-8 text-sm"
                                  placeholder="0"
                                />
                              </td>
                              <td className="py-1.5 px-1">
                                {period.room_pricing.length > 1 && (
                                  <Button 
                                    type="button" 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={() => removeRoomFromPeriod(periodIdx, roomIdx)}
                                    className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                          
                          {/* Extra Bed Row */}
                          <tr className="bg-blue-50/50">
                            <td className="py-1.5 px-3 font-medium text-blue-800 text-sm">Extra Bed</td>
                            <td className="py-1.5 px-1">
                              <Input 
                                type="number"
                                value={period.extra_bed.ep}
                                onChange={e => updateExtraPricingInPeriod(periodIdx, 'extra_bed', 'ep', e.target.value)}
                                className="bg-white border-gray-200 text-center h-8 text-sm"
                                placeholder="0"
                              />
                            </td>
                            <td className="py-1.5 px-1">
                              <Input 
                                type="number"
                                value={period.extra_bed.cp}
                                onChange={e => updateExtraPricingInPeriod(periodIdx, 'extra_bed', 'cp', e.target.value)}
                                className="bg-white border-gray-200 text-center h-8 text-sm"
                                placeholder="0"
                              />
                            </td>
                            <td className="py-1.5 px-1">
                              <Input 
                                type="number"
                                value={period.extra_bed.map}
                                onChange={e => updateExtraPricingInPeriod(periodIdx, 'extra_bed', 'map', e.target.value)}
                                className="bg-white border-gray-200 text-center h-8 text-sm"
                                placeholder="0"
                              />
                            </td>
                            <td className="py-1.5 px-1">
                              <Input 
                                type="number"
                                value={period.extra_bed.ap}
                                onChange={e => updateExtraPricingInPeriod(periodIdx, 'extra_bed', 'ap', e.target.value)}
                                className="bg-white border-gray-200 text-center h-8 text-sm"
                                placeholder="0"
                              />
                            </td>
                            <td></td>
                          </tr>
                          
                          {/* CWB Row */}
                          <tr className="bg-green-50/50">
                            <td className="py-1.5 px-3 font-medium text-green-800 text-sm">CWB</td>
                            <td className="py-1.5 px-1">
                              <Input 
                                type="number"
                                value={period.cwb.ep}
                                onChange={e => updateExtraPricingInPeriod(periodIdx, 'cwb', 'ep', e.target.value)}
                                className="bg-white border-gray-200 text-center h-8 text-sm"
                                placeholder="0"
                              />
                            </td>
                            <td className="py-1.5 px-1">
                              <Input 
                                type="number"
                                value={period.cwb.cp}
                                onChange={e => updateExtraPricingInPeriod(periodIdx, 'cwb', 'cp', e.target.value)}
                                className="bg-white border-gray-200 text-center h-8 text-sm"
                                placeholder="0"
                              />
                            </td>
                            <td className="py-1.5 px-1">
                              <Input 
                                type="number"
                                value={period.cwb.map}
                                onChange={e => updateExtraPricingInPeriod(periodIdx, 'cwb', 'map', e.target.value)}
                                className="bg-white border-gray-200 text-center h-8 text-sm"
                                placeholder="0"
                              />
                            </td>
                            <td className="py-1.5 px-1">
                              <Input 
                                type="number"
                                value={period.cwb.ap}
                                onChange={e => updateExtraPricingInPeriod(periodIdx, 'cwb', 'ap', e.target.value)}
                                className="bg-white border-gray-200 text-center h-8 text-sm"
                                placeholder="0"
                              />
                            </td>
                            <td></td>
                          </tr>
                          
                          {/* CNB Row */}
                          <tr className="bg-amber-50/50">
                            <td className="py-1.5 px-3 font-medium text-amber-800 text-sm">CNB</td>
                            <td className="py-1.5 px-1">
                              <Input 
                                type="number"
                                value={period.cnb.ep}
                                onChange={e => updateExtraPricingInPeriod(periodIdx, 'cnb', 'ep', e.target.value)}
                                className="bg-white border-gray-200 text-center h-8 text-sm"
                                placeholder="0"
                              />
                            </td>
                            <td className="py-1.5 px-1">
                              <Input 
                                type="number"
                                value={period.cnb.cp}
                                onChange={e => updateExtraPricingInPeriod(periodIdx, 'cnb', 'cp', e.target.value)}
                                className="bg-white border-gray-200 text-center h-8 text-sm"
                                placeholder="0"
                              />
                            </td>
                            <td className="py-1.5 px-1">
                              <Input 
                                type="number"
                                value={period.cnb.map}
                                onChange={e => updateExtraPricingInPeriod(periodIdx, 'cnb', 'map', e.target.value)}
                                className="bg-white border-gray-200 text-center h-8 text-sm"
                                placeholder="0"
                              />
                            </td>
                            <td className="py-1.5 px-1">
                              <Input 
                                type="number"
                                value={period.cnb.ap}
                                onChange={e => updateExtraPricingInPeriod(periodIdx, 'cnb', 'ap', e.target.value)}
                                className="bg-white border-gray-200 text-center h-8 text-sm"
                                placeholder="0"
                              />
                            </td>
                            <td></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      EP = Room Only | CP = Breakfast | MAP = Breakfast + Lunch/Dinner | AP = All Meals
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Rate Period Button */}
              <Button 
                type="button" 
                variant="outline" 
                onClick={addRatePeriod}
                className="w-full border-dashed border-orange-300 text-orange-600 hover:bg-orange-50"
                data-testid="add-rate-period-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Another Rate Period
              </Button>

              {/* Amenities - Only remaining field */}
              <div>
                <Label>Amenities</Label>
                <Input 
                  value={hotelForm.amenities} 
                  onChange={e => setHotelForm({...hotelForm, amenities: e.target.value})} 
                  className="bg-gray-50 border-gray-200" 
                  placeholder="WiFi, Pool, Spa, Gym, Restaurant, Parking..."
                  data-testid="hotel-amenities-input"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => { setShowHotelModal(false); resetHotelForm(); }} data-testid="hotel-cancel-btn">
                  Cancel
                </Button>
                <Button onClick={submitHotel} className="bg-orange-600 hover:bg-orange-700" data-testid="hotel-save-btn">
                  <Save className="w-4 h-4 mr-2" /> {editingItem ? 'Update' : 'Save'} Hotel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

// Icon components for card
const Clock = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;

export default AdminResources;
